import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const KEY = "gauntlet_tour_done";
const STEPS = [
  { target: "stats", title: "Command Center", body: "Live readiness across every test you've run: average score, pass rate and compliance breaches at a glance." },
  { target: "nav-new", title: "New Test Wizard", body: "Pick a scenario, paste your call script, choose up to 8 adversarial customer personas, and launch." },
  { target: "recent", title: "Arena", body: "Each persona calls your agent in parallel. Watch transcripts stream, moods shift, and judges score in real time." },
  { target: "demo-toggle", title: "Report & Demo mode", body: "Every run ends in a Pilot Readiness report with break points, Script Doctor fixes and before/after. Flip Demo mode to replay a seeded run instantly." },
];

export const Tour = () => {
  const loc = useLocation();
  const [step, setStep] = useState(() => (localStorage.getItem(KEY) ? -1 : 0));
  const [rect, setRect] = useState(null);
  const active = step >= 0 && loc.pathname === "/";

  useEffect(() => {
    if (!active) return undefined;
    const measure = () => {
      const el = document.querySelector(`[data-tour="${STEPS[step].target}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const t = setTimeout(measure, 150);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [step, active]);

  const finish = () => { localStorage.setItem(KEY, "1"); setStep(-1); };
  if (!active) return null;
  const pad = 8;
  const s = STEPS[step];
  const top = rect ? Math.min(rect.bottom + 16, window.innerHeight - 220) : window.innerHeight / 2 - 100;
  const left = rect ? Math.min(Math.max(rect.left, 16), window.innerWidth - 376) : window.innerWidth / 2 - 180;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="guided-tour" role="dialog" aria-label="Guided tour">
      <motion.div layout transition={{ type: "spring", stiffness: 200, damping: 26 }}
        className="absolute rounded-2xl ring-2 ring-cyan-400/80"
        style={rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: "0 0 0 9999px rgba(5,6,10,0.78)" }
          : { top: 0, left: 0, width: 0, height: 0, boxShadow: "0 0 0 9999px rgba(5,6,10,0.78)" }} />
      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
          className="absolute w-[360px] panel p-5 shadow-2xl" style={{ top, left }}>
          <p className="eyebrow mb-2">Step {step + 1} of {STEPS.length}</p>
          <h3 className="text-lg font-semibold mb-1">{s.title}</h3>
          <p className="text-sm text-white/65 leading-relaxed">{s.body}</p>
          <div className="mt-4 flex items-center justify-between">
            <button onClick={finish} className="text-xs text-white/50 hover:text-white" data-testid="tour-skip">Skip tour</button>
            <div className="flex gap-2">
              {step > 0 && <button onClick={() => setStep(step - 1)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="tour-back">Back</button>}
              <button onClick={() => (step === STEPS.length - 1 ? finish() : setStep(step + 1))} className="btn-primary !py-1.5 !px-4 text-xs" data-testid="tour-next">
                {step === STEPS.length - 1 ? "Got it" : "Next"}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
